import { V4, debug } from '@moleculeone/utils';

import * as config from '../config';
import { positionsDataTest } from '../checks';
import { align, flipXPositions } from '../geometry';
import { circumscribedPolygon } from './circumscribe';
import { overlapScore, isGroupKey, mergeGroups } from './utils';
import { vertexCycleSearch } from '../selectors';

function mergeGroupsPair(positionsData, group1Idx, group2Idx) {
  const group1Positions = positionsData.groupPositions[group1Idx];
  const group2Positions = positionsData.groupPositions[group2Idx];

  const alignedCandidate1 = align(group1Positions, group2Positions);
  const flippedPositions = flipXPositions(group1Positions);
  const alignedCandidate2 = align(flippedPositions, group2Positions);

  let chosenCandidate;
  if (
    overlapScore(positionsData.mol, alignedCandidate1, group2Positions) >
    overlapScore(positionsData.mol, alignedCandidate2, group2Positions)
  ) {
    chosenCandidate = alignedCandidate1;
  } else {
    chosenCandidate = alignedCandidate2;
  }

  mergeGroups(positionsData, [chosenCandidate, group2Positions], [group1Idx, group2Idx]);
}

function mergeGroupsCycle(positionsData, groupsCycle, commonAtoms) {
  let { bestScore, bestOptions } = groupsCycleAlignment(positionsData, groupsCycle, commonAtoms, 1.0);
  // very low score suggests high overlap - one way of avoiding this is increasing ring size
  if (bestScore < 0.25) {
    const { bestScore: enlargedScore, bestOptions: enlargedOptions } = groupsCycleAlignment(
      positionsData,
      groupsCycle,
      commonAtoms,
      1.35
    );
    if (enlargedScore > bestScore) {
      bestOptions = enlargedOptions;
    }
  }
  mergeGroups(positionsData, bestOptions, groupsCycle);
}

function groupsCycleAlignment(positionsData, groupsCycle, commonAtoms, scale) {
  const polygonEdgeLengths = [];
  for (const groupIdx of groupsCycle) {
    const groupPositions = positionsData.groupPositions[groupIdx];
    const [atomIdxBefore, atomIdxAfter] = commonAtoms[groupIdx];
    const edgeLength = V4.dist(groupPositions[atomIdxBefore], groupPositions[atomIdxAfter]);
    polygonEdgeLengths.push(edgeLength);
  }

  const verticesPositionsList = circumscribedPolygon(polygonEdgeLengths.map(l => l * scale));
  const newCommonAtomsPositions = {};
  for (const [ordinal, vertexPos] of verticesPositionsList.entries()) {
    const [cycleAtomIdx] = commonAtoms[groupsCycle[ordinal]];
    newCommonAtomsPositions[cycleAtomIdx] = vertexPos;
  }

  const newPositionsOptions = groupsCycle.map(groupIdx => {
    const groupPositions = positionsData.groupPositions[groupIdx];
    const result = [align(groupPositions, newCommonAtomsPositions)];
    if (Object.keys(groupPositions).length > 2) {
      const flippedPositions = flipXPositions(groupPositions);
      result.push(align(flippedPositions, newCommonAtomsPositions));
    } // if len == 2, flipped and aligned option should be the same as the original one
    return result;
  });

  return pickBestOptions(positionsData.mol, newPositionsOptions);
}

function pickBestOptions(mol, positionOptionsLists) {
  const pickedOptionOrdinals = Array(positionOptionsLists.length).fill(0);

  function optionCompatibility(groupOrdinal, optionOrdinal) {
    const pickedGroup = positionOptionsLists[groupOrdinal][optionOrdinal];
    let minDist = Number.POSITIVE_INFINITY;
    for (const [otherGroupOrdinal, otherOptionOrdinal] of pickedOptionOrdinals.entries()) {
      if (otherGroupOrdinal === groupOrdinal) {
        continue;
      }
      const otherGroup = positionOptionsLists[otherGroupOrdinal][otherOptionOrdinal];
      minDist = Math.min(minDist, overlapScore(mol, pickedGroup, otherGroup));
    }
    return minDist;
  }

  for (let i = 0; i < config.drawing.cycleRefinements; i++) {
    for (let groupOrdinal = 0; groupOrdinal < positionOptionsLists.length; groupOrdinal++) {
      if (positionOptionsLists[groupOrdinal].length === 1) {
        // only one option - we have no choice
        continue;
      }
      const optionToCompat = {};
      const optionsCount = positionOptionsLists[groupOrdinal].length;
      for (let optionOrdinal = 0; optionOrdinal < optionsCount; optionOrdinal++) {
        optionToCompat[optionOrdinal] = optionCompatibility(groupOrdinal, optionOrdinal);
      }
      // pick option with greatest compatibility
      pickedOptionOrdinals[groupOrdinal] = Object.keys(optionToCompat).reduce((a, b) =>
        optionToCompat[a] > optionToCompat[b] ? a : b
      );
    }
  }

  const bestOptions = pickedOptionOrdinals.map(
    (optionOrdinal, groupOrdinal) => positionOptionsLists[groupOrdinal][optionOrdinal]
  );
  const bestScore = pickedOptionOrdinals
    .map((optionOrdinal, groupOrdinal) => optionCompatibility(groupOrdinal, optionOrdinal))
    .reduce((a, b) => Math.min(a, b), Number.POSITIVE_INFINITY);
  return { bestOptions, bestScore };
}

// returns [atomIdx, groupIdx, atomIdx..., groupIdx, atomIdx] list where last one equals the first
function smallestBipartiteCycle(groupGraph) {
  const generators = {};
  for (const atomIdx of Object.keys(groupGraph)) {
    if (!isGroupKey(atomIdx)) {
      generators[atomIdx] = vertexCycleSearch(atomIdx, idx => groupGraph[idx]);
    }
  }

  while (Object.keys(generators).length !== 0) {
    for (const [atomIdx, search] of Object.entries(generators)) {
      const { value, done } = search.next();
      if (value) {
        value.push(value[0]);
        return value;
      }
      if (done) {
        delete generators[atomIdx];
      }
    }
  }
  return null;
}

export function mergeGroupCycles(positionsData) {
  let cycle = smallestBipartiteCycle(positionsData.groupGraph);
  while (cycle != null) {
    // 4 element cycle - last element is equal to the first one.
    if (cycle.length === 5) {
      // two groups having at least two common atoms
      // we will align them to minimise distances between common atoms
      // and average their positions
      const [, group1Key, , group2Key] = cycle;
      mergeGroupsPair(positionsData, group1Key, group2Key);
    } else {
      // every two consecutive groups in cycle have exactly one common atom -
      // otherwise, they would form 4 element cycle handled above or would not
      // form a cycle at all
      const commonAtoms = {};
      const groupsCycle = [];
      for (let i = 1; i < cycle.length; i += 2) {
        const groupIdx = cycle[i];
        const atomIdxBefore = cycle[i - 1];
        const atomIdxAfter = cycle[i + 1];
        commonAtoms[groupIdx] = [atomIdxBefore, atomIdxAfter];
        groupsCycle.push(groupIdx);
      }
      mergeGroupsCycle(positionsData, groupsCycle, commonAtoms);
    }
    cycle = smallestBipartiteCycle(positionsData.groupGraph);
  }
  debug(positionsDataTest, positionsData);
}
